/**
 * Study-session TIME tracking — the "real activity" clock behind the
 * leaderboard's Time ranking and the session-summary "time spent" stat.
 *
 * ### What counts as study time
 * Wall-clock time while the study screen is in the foreground
 * (`visibilityState === 'visible'` on web, `AppState === 'active'` on
 * mobile), with a **30s grace window** per hide event: a quick tab
 * switch / app switch of ≤ 30s is counted in full, anything longer
 * credits at most 30s. So "closed the laptop overnight mid-Fast-Review"
 * adds ≤ 30s instead of 8 hours, while a 5s notification peek costs
 * nothing. Visible-but-idle time counts by design — we deliberately do
 * NOT do input-level idle detection (out of scope for v1).
 *
 * ### Architecture
 * The tracker core is pure (injectable `now`, no timers, no listeners)
 * so the grace / suspend arithmetic is unit-testable. Platform hooks own
 * the event wiring and call `markVisible` / `markHidden` / `heartbeat`:
 *
 *   - web:    `document.visibilitychange` (+ `pagehide` flush) + a ~5s
 *             heartbeat interval.
 *   - mobile: `AppState` change events + the same heartbeat.
 *
 * The heartbeat exists for ONE reason: a device can suspend the JS VM
 * without delivering a visibility event first (classic case: laptop lid
 * closed while the tab stays "visible"). On wake, the gap between now
 * and the last heartbeat betrays the suspension, and the tracker credits
 * that gap under the same 30s cap as a hide/show pair. `elapsedMs()` and
 * `takeDeltaMs()` self-heal through the same check, so even a read that
 * races ahead of the first post-wake heartbeat can never observe (or
 * drain!) a phantom 8-hour segment.
 *
 * ### How the time reaches the server (shared decks only)
 * Two wires, one accumulator, each ms delivered exactly once:
 *
 *   1. PIGGYBACK — `pushScoreToAllGroups` (and the single-group
 *      `pushScore`) drain the ambient tracker and attach the pending ms
 *      as the optional `timeDeltaMs` field on the existing
 *      `sg-score-delta` frame. Per-card flows (SR / FR / quiz / exam)
 *      therefore ship time at card-commit granularity for free.
 *   2. `sg-time-delta` — a dedicated fire-and-forget frame sent by
 *      `flushStudyTimeToGroups`, used by the platform hooks' periodic
 *      flush (~60s), the on-hide flush, and the end-of-session flush.
 *      This is what makes time land live for flows with no per-card
 *      commits (oral exam, case study — their MP arrives only after
 *      grading) and what catches the "tail" between the last card
 *      commit and an early exit.
 *
 * Both wires mutate the same `SGScore` row server-side (see
 * `applyTimeDelta` / the `timeDeltaMs` branch of `applyScoreDelta` in
 * `flashka-web/party/server/group/score-delta-handler.ts`). Time NEVER
 * touches MP or the streak.
 *
 * ### Personal decks
 * The tracker runs for EVERY study session (the ending screens read
 * `elapsedMs()` regardless of deck kind); only the wire delivery is
 * gated on linked groups — `flushStudyTimeToGroups([])` is a no-op and
 * the piggyback drains nothing when no push happens.
 */

import { MAX_TIME_DELTA_MS } from '../../sync-engine/schema/sg-score-wire';

/**
 * Grace window per hide event: hidden gaps ≤ this are credited in full,
 * longer gaps credit exactly this much. Anti-abuse guarantee: one hide
 * event can never add more than 30s of non-visible time.
 */
export const STUDY_TIME_GRACE_MS = 30_000;

/**
 * A gap between consecutive heartbeats larger than this means the JS VM
 * was suspended (device sleep / hard tab freeze) without a visibility
 * event. Must be comfortably above the heartbeat interval (~5s) so
 * ordinary timer jitter never trips it, and well below the grace window
 * ceiling so the correction stays fine-grained.
 */
export const STUDY_TIME_SUSPEND_GAP_MS = 15_000;

export type StudyTimeTracker = {
  /** Foreground gained (visibilitychange → visible / AppState → active). */
  markVisible(): void;
  /** Foreground lost (visibilitychange → hidden / AppState → background). */
  markHidden(): void;
  /**
   * Periodic liveness tick from the platform hook (~5s). Also invoked
   * internally by `elapsedMs`/`takeDeltaMs`, so reads self-heal after a
   * VM suspension even before the next scheduled tick fires.
   */
  heartbeat(): void;
  /** Total tracked ms so far (accumulated + the live running segment). */
  elapsedMs(): number;
  /**
   * Drain: ms accumulated since the previous drain. Monotonic — each
   * tracked ms is returned exactly once across all `takeDeltaMs` calls.
   * Returns 0 when nothing new accrued.
   */
  takeDeltaMs(): number;
  /** Freeze the tracker (session over). Every later call is a no-op read. */
  stop(): void;
};

export type CreateStudyTimeTrackerOptions = {
  graceMs?: number;
  suspendGapMs?: number;
  /** Clock injection for tests. Defaults to `Date.now`. */
  now?: () => number;
  /**
   * Whether the surface is foreground at creation. Platform hooks pass
   * the real visibility so a tab restored in the background doesn't
   * accrue a phantom running segment.
   */
  startVisible?: boolean;
};

export function createStudyTimeTracker(
  options: CreateStudyTimeTrackerOptions = {},
): StudyTimeTracker {
  const now = options.now ?? Date.now;
  const graceMs = options.graceMs ?? STUDY_TIME_GRACE_MS;
  const suspendGapMs = options.suspendGapMs ?? STUDY_TIME_SUSPEND_GAP_MS;

  /** Fully-counted ms from closed segments (+ grace credits). */
  let accumulatedMs = 0;
  /** Start of the live visible segment, null while hidden/stopped. */
  let runningSince: number | null = options.startVisible === false ? null : now();
  /**
   * When the surface went hidden — anchor for the resume grace credit.
   * Deliberately null for a tracker created in the background: grace
   * covers interruptions of a RUNNING session, never the stretch before
   * the surface was first seen.
   */
  let hiddenAt: number | null = null;
  /** Last moment we KNOW the VM was awake — suspension detector anchor. */
  let lastSeenAt = now();
  /** High-water mark of ms already handed out via `takeDeltaMs`. */
  let drainedMs = 0;
  let stopped = false;

  /**
   * Detect a VM suspension that slipped past the visibility events: the
   * time between `lastSeenAt` and now was NOT observably-foreground, so
   * split the running segment at `lastSeenAt` and credit the dark gap
   * under the same cap as a hide/show pair.
   */
  const healSuspension = (t: number): void => {
    if (runningSince !== null && t - lastSeenAt > suspendGapMs) {
      accumulatedMs +=
        Math.max(0, lastSeenAt - runningSince) + Math.min(t - lastSeenAt, graceMs);
      runningSince = t;
    }
    lastSeenAt = t;
  };

  // Free function (not a method) so `takeDeltaMs` works after
  // destructuring — no `this` dependence anywhere on the tracker.
  const elapsedMsNow = (): number => {
    if (!stopped) healSuspension(now());
    return accumulatedMs + (runningSince !== null ? now() - runningSince : 0);
  };

  return {
    markVisible() {
      if (stopped || runningSince !== null) return;
      const t = now();
      if (hiddenAt !== null) {
        // Grace credit: short hides count in full, long ones cap out.
        accumulatedMs += Math.min(t - hiddenAt, graceMs);
        hiddenAt = null;
      }
      runningSince = t;
      lastSeenAt = t;
    },
    markHidden() {
      if (stopped || runningSince === null) return;
      const t = now();
      healSuspension(t);
      accumulatedMs += t - (runningSince as number);
      runningSince = null;
      hiddenAt = t;
      lastSeenAt = t;
    },
    heartbeat() {
      if (stopped) return;
      healSuspension(now());
    },
    elapsedMs() {
      return elapsedMsNow();
    },
    takeDeltaMs() {
      const elapsed = Math.floor(elapsedMsNow());
      const delta = elapsed - drainedMs;
      if (delta <= 0) return 0;
      drainedMs = elapsed;
      return delta;
    },
    stop() {
      if (stopped) return;
      healSuspension(now());
      if (runningSince !== null) {
        accumulatedMs += now() - runningSince;
        runningSince = null;
      }
      stopped = true;
    },
  };
}

// ---------------------------------------------------------------------------
// Ambient active tracker — one study session at a time.
//
// The piggyback drain lives inside the shared push facade
// (`leaderboard.ts`), which has no access to React context. A tiny
// module-level registry bridges that gap: the platform session hook
// registers its tracker on mount and unregisters on unmount. Single
// active session is an existing app invariant (one study screen at a
// time); the unregister guard below keeps a stale unmount from clearing
// a newer session's tracker during fast navigation.
// ---------------------------------------------------------------------------

let activeTracker: StudyTimeTracker | null = null;

export function setActiveStudyTimeTracker(tracker: StudyTimeTracker): void {
  activeTracker = tracker;
}

/** Unregister `tracker` — no-op when a newer session already took over. */
export function clearActiveStudyTimeTracker(tracker: StudyTimeTracker): void {
  if (activeTracker === tracker) activeTracker = null;
}

export function getActiveStudyTimeTracker(): StudyTimeTracker | null {
  return activeTracker;
}

/**
 * Drain a tracker for a wire send, clamped to the per-message ceiling
 * shared with the server (`MAX_TIME_DELTA_MS`). Ms beyond the clamp are
 * dropped, not carried — a legit client flushing every ~60s sits orders
 * of magnitude below the ceiling, so overflow only occurs for
 * pathological states we'd rather not credit anyway.
 *
 * Defaults to the ambient tracker (the piggyback path has no tracker in
 * scope). The platform hooks pass their OWN instance instead: on mobile
 * a study screen can stay mounted while another one is focused (tab
 * routes, stacked root routes), and draining the ambient slot from a
 * blurred screen's flush would misattribute the focused session's time
 * to the blurred screen's groups.
 *
 * Returns 0 when no tracker is available or nothing accrued.
 */
export function takeStudyTimeDeltaMsForWire(
  tracker: StudyTimeTracker | null = getActiveStudyTimeTracker(),
): number {
  const delta = tracker?.takeDeltaMs() ?? 0;
  if (delta <= 0) return 0;
  return Math.min(delta, MAX_TIME_DELTA_MS);
}

// ---------------------------------------------------------------------------
// Display formatting — `2h 28min` / `1min` / `0min`, hours unbounded
// (`143h 23min`), NEVER days. The number and the unit are separate parts
// because the leaderboard row styles them at different font sizes
// (16px value / 12px unit — Figma node 29099:19457).
// ---------------------------------------------------------------------------

export type StudyTimeDisplayPart = {
  value: string;
  unit: 'h' | 'min';
};

/**
 * Break tracked ms into display parts. Minutes floor (59.9s → `0min`);
 * the minutes part is always present, the hours part only when ≥ 1h.
 */
export function studyTimeDisplayParts(timeMs: number): StudyTimeDisplayPart[] {
  const totalMinutes = Math.floor(Math.max(0, timeMs) / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours <= 0) return [{ value: `${minutes}`, unit: 'min' }];
  return [
    { value: `${hours}`, unit: 'h' },
    { value: `${minutes}`, unit: 'min' },
  ];
}
